import { requestIdleCallback } from '@theme/utils'

const EventConfig = {
  bubbleEvents: ['focus', 'blur'],
  delegatedEvents: [
    'click',
    'change',
    'select',
    'focus',
    'blur',
    'submit',
    'input',
    'keydown',
    'keyup',
    'toggle'
  ],
  expensiveEvents: ['pointerenter', 'pointerleave']
}

export class Component extends HTMLElement {
  refs = {}
  requiredRefs

  connectedCallback() {
    registerEventListeners()
    this.#updateRefs()
    this.mounted?.()
    requestIdleCallback(() => {
      this.#mutationObserver.observe(this, {
        attributeFilter: ['ref'],
        attributeOldValue: true,
        attributes: true,
        childList: true,
        subtree: true
      })
    })
  }

  updatedCallback() {
    this.#mutationObserver.takeRecords()
    this.#updateRefs()
    this.updated?.()
  }

  disconnectedCallback() {
    this.beforeUnmount?.()
    this.#mutationObserver.disconnect()
    this.unmounted?.()
  }

  #updateRefs() {
    const refs = {}
    const elements = new Set()
    for (const element of this.querySelectorAll('[ref]')) {
      if (this.#isDescendant(element)) {
        elements.add(element)
      }
    }

    for (const ref of elements) {
      const refName = ref.getAttribute('ref') ?? ''
      const isArray = refName.endsWith('[]')
      const path = isArray ? refName.slice(0, -2) : refName

      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : []
        array.push(ref)
        refs[path] = array
      } else {
        refs[path] = ref
      }
    }

    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this)
        }
      }
    }

    this.refs = refs
  }

  #mutationObserver = new MutationObserver((mutations) => {
    if (
      mutations.some(
        (m) =>
          (m.type === 'attributes' && this.#isDescendant(m.target)) ||
          (m.type === 'childList' &&
            [...m.addedNodes, ...m.removedNodes].some(this.#isDescendant))
      )
    ) {
      this.#updateRefs()
    }
  })

  #isDescendant = (node) => getClosestComponent(getAncestor(node)) === this
}

function getAncestor(node) {
  if (node.parentNode) {
    return node.parentNode
  }
  const root = node.getRootNode()
  if (root instanceof ShadowRoot) {
    return root.host
  }
  return null
}

function getClosestComponent(node) {
  if (!node) {
    return null
  }
  if (node instanceof Component) {
    return node
  }
  if (
    node instanceof HTMLElement &&
    node.tagName.toLowerCase().endsWith('-component')
  ) {
    return node
  }
  const ancestor = getAncestor(node)
  if (ancestor) {
    return getClosestComponent(ancestor)
  }
  return null
}

function getDelegatedEventTarget(event) {
  const target = event.composedPath?.()[0] ?? event.target
  if (!(target instanceof Element)) {
    return null
  }
  if (target.hasAttribute(`on:${event.type}`)) {
    return target
  }
  if (EventConfig.expensiveEvents.includes(event.type)) {
    return null
  }
  return event.bubbles || EventConfig.bubbleEvents.includes(event.type)
    ? target.closest(`[on\\:${event.type}]`)
    : null
}

function proxyEventTarget(event, element) {
  if (event.target === element) {
    return event
  }
  return new Proxy(event, {
    get(target, property) {
      if (property === 'target') {
        return element
      }
      const value = Reflect.get(target, property)
      return typeof value === 'function' ? value.bind(target) : value
    }
  })
}

function resolveInstance(element, selector) {
  if (!selector) {
    return getClosestComponent(element)
  }
  return selector.startsWith('#')
    ? document.querySelector(selector)
    : element.closest(selector)
}

function dispatchToComponent(element, attribute, proxiedEvent) {
  const value = element.getAttribute(attribute) ?? ''
  let [selector, method] = value.split('/')
  const matches = value.match(/([/?][^/?]+)([/?][^/?]+)$/)
  const parsedEventData = matches ? matches[2] : null

  const instance = resolveInstance(element, selector)
  if (!(instance instanceof Component && method)) {
    return
  }

  method = method.replace(/\?.*/, '')
  const callback = instance[method]
  if (typeof callback !== 'function') {
    return
  }

  const args = [proxiedEvent]
  if (parsedEventData) {
    args.unshift(parseData(parsedEventData))
  }
  callback.call(instance, ...args)
}

let initialized = false

function registerEventListeners() {
  if (initialized) {
    return
  }
  initialized = true

  for (const eventName of [
    ...EventConfig.delegatedEvents,
    ...EventConfig.expensiveEvents
  ]) {
    const attribute = `on:${eventName}`

    document.addEventListener(
      eventName,
      (event) => {
        const element = getDelegatedEventTarget(event)
        if (!element) {
          return
        }
        const proxiedEvent = proxyEventTarget(event, element)
        dispatchToComponent(element, attribute, proxiedEvent)
      },
      { capture: true }
    )
  }
}

function parseData(str) {
  const delimiter = str[0]
  const raw = str.slice(1)
  return delimiter === '?'
    ? Object.fromEntries(
        Array.from(new URLSearchParams(raw).entries()).map(([key, value]) => [
          key,
          parseValue(value)
        ])
      )
    : parseValue(raw)
}

function parseValue(str) {
  if (str === 'true') {
    return true
  }
  if (str === 'false') {
    return false
  }
  const maybeNumber = Number(str)
  if (!Number.isNaN(maybeNumber) && str.trim() !== '') {
    return maybeNumber
  }
  return str
}

class MissingRefError extends Error {
  constructor(ref, component) {
    super(
      `Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`
    )
  }
}
